import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as GroupSelector from '../../../store/grouplist/grouplist.selectors';
import * as GroupAction from '../../../store/grouplist/grouplist.actions';
import * as GroupInterface from '../../../domain/group.interface';
import { getAssetUrl } from '../../../utils/GetAssetUrl';
import moment from 'moment';
import { WishCardComponent, PiggyComponent } from '../../atoms/atoms.index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ERoutes } from '../../../assets/constants/routes';
import { replacePathParams } from '../../../utils/NavigateWithParams';
import {
  NavbarComponent,
  NavigationMenuComponent,
  NewEditWishModalFormComponent,
  TpvModalFormComponent,
  SetHasPaidModalFormComponent,
} from '../../organisms/organisms.index';
import { IWish } from '../../../domain/wish.interface';
import { returnNotification } from '../../../store/render-controller/render-controller.actions';
import {
  ENotification,
  INotification,
} from '../../../domain/notification.interface';
import {
  ERefound,
  IActiveGroupWishesProps,
} from '../../../domain/group.interface';
import { socialProjectsLink } from '../../../assets/constants/external-links';
import {
  setAsPaidGroupMember,
  sendGroupEmail,
} from '../../../store/grouplist/grouplist.actions';
import './ActiveGroup.component.scss';
import { makeSelectCurrentUser } from '../../../store/current-user/current-user.selectors';
import { EUserType } from '../../../domain/typeahead.interfaces';
import { formatNumberToCurrency } from '../../../utils/FormNormalizers';
import {
  TFeeType,
  EFeeType,
  EGroupEmails,
  TGroupEmail,
} from '../../../domain/group.interface';
import history from '../../../store/history/history';
import { ERadio } from '../../../domain/input.interface';

function scrollToTop() {
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  } catch (e) {
    window.scrollTo(0, 0);
  }
}

function RenderNav(
  config: GroupInterface.IActiveGroupConfig[],
  setActiveIdx: any,
  activeIdx: number,
) {
  return config.map((el: GroupInterface.IActiveGroupConfig, idx: number) => {
    const { name } = el;
    const active: boolean = activeIdx === idx;
    return (
      <li className='nav-item' key={idx} onClick={() => setActiveIdx(idx)}>
        <Link
          className={`nav-link ${active ? 'active' : ''}`}
          id={`${name}-tab`}
          data-toggle='pill'
          to={`#${name}`}
          role='tab'
          aria-controls={`${name}`}
          aria-selected={active ? 'true' : 'false'}
        >
          <div className='desktop-nav-item'>
            <FormattedMessage id={`pages.active-group.${name}`} />
          </div>
          <div className='mobile-nav-item'>
            {el.icon ? (
              <FontAwesomeIcon icon={el.icon} />
            ) : (
              <div
                className='image-container'
                style={{
                  backgroundImage: `url(${getAssetUrl.icons(el.image)})`,
                }}
              />
            )}
          </div>
        </Link>
      </li>
    );
  });
}

function RenderTab(activeTab: GroupInterface.IActiveGroupConfig) {
  const { name, Component, props } = activeTab;
  return (
    <div
      key={name}
      className='tab-pane fade show active'
      id={`${name}`}
      role='tabpanel'
      aria-labelledby={`${name}-tab`}
    >
      <Component {...props} />
    </div>
  );
}

function RenderMembers(props: GroupInterface.IActiveGroupMembersProps) {
  const { admin, currentUserId, memberList } = props;

  return (
    <div className='tab-container active-group-members-container'>
      {!!admin && (
        <div
          className={`minicard ${
            currentUserId === admin.id
              ? `user-type-${EUserType.SELF}`
              : `user-type-${EUserType.CONTACT}`
          }`}
        >
          <div className='minicard-image'>
            {!!admin.userPhoto ? (
              <div
                style={{
                  backgroundImage: `url(${getAssetUrl.imagesStatic(
                    admin.userPhoto,
                  )})`,
                }}
              />
            ) : (
              <div className='icon-container d-flex justify-container-center align-items-center'>
                <FontAwesomeIcon icon='user' />
              </div>
            )}
          </div>
          <div className='minicard-info'>
            <h6 className='capitalize'>
              {!!admin.name ? admin.name : admin.nickname}
            </h6>
            <p>
              (<FormattedMessage id={`common.user-type-${EUserType.ADMIN}`} />)
            </p>
          </div>
        </div>
      )}
      {!!memberList.length &&
        memberList.map((member: GroupInterface.IGroupMember) => {
          const {
            name,
            nickname,
            userPhoto: image,
            active,
            isExternal,
          } = member;
          return (
            <div
              key={nickname}
              className={`minicard ${
                currentUserId === member.id
                  ? `user-type-${EUserType.SELF}`
                  : active
                  ? `user-type-${EUserType.CONTACT}`
                  : `user-type-${EUserType.INACTIVE}`
              }`}
            >
              <div className='minicard-image'>
                {image ? (
                  <div
                    style={{
                      backgroundImage: `url(${getAssetUrl.imagesStatic(
                        image,
                      )})`,
                    }}
                  />
                ) : (
                  <div className='icon-container d-flex justify-container-center align-items-center'>
                    <FontAwesomeIcon icon='user' />
                  </div>
                )}
              </div>
              <div className='minicard-info'>
                <h6 className='capitalize'>{!!name ? name : nickname}</h6>
                {isExternal ? (
                  <p>
                    ({' '}
                    <FormattedMessage
                      id={`common.user-type-${EUserType.EXTERNAL}`}
                    />{' '}
                    )
                  </p>
                ) : active ? (
                  !!name ? (
                    <p>{nickname}</p>
                  ) : null
                ) : (
                  <p>
                    ({' '}
                    <FormattedMessage
                      id={`common.user-type-${EUserType.INACTIVE}`}
                    />{' '}
                    )
                  </p>
                )}
              </div>
            </div>
          );
        })}
      {!memberList.length && (
        <p className='text-primary'>
          <FormattedMessage id='pages.active-group.members.no_data' />
        </p>
      )}
    </div>
  );
}

function WishPurchasedCard(props: { wishPurchased: IWish }) {
  const { name, purchaseAmount } = props.wishPurchased;
  return (
    <div className='mb-3'>
      <h4 className='subtitle'>
        <FormattedMessage id='pages.active-group.wishes.yet-purchased-title' />
      </h4>
      <div className='purchased-wish-card'>
        <div className='left-container'>
          <div
            className='image-container'
            style={{
              backgroundImage: `url(${getAssetUrl.icons(
                'REGALO_GRUPO_COLOR.svg',
              )})`,
            }}
          ></div>
          <div className='price-container mobile'>
            <p className='text-secondary mb-0'>
              <FormattedMessage id='common.price' />
              {': '}
              <br />
              <span className='text-primary'>
                {formatNumberToCurrency(purchaseAmount as number)}
              </span>
            </p>
          </div>
        </div>
        <div className='right-container'>
          <div className='text-container'>
            <h5 className='subtitle'>{name}</h5>
          </div>
          <div className='price-container desktop'>
            <p className='text-secondary mb-0'>
              <FormattedMessage id='common.price' />
              {': '}
              <span className='text-primary'>
                {formatNumberToCurrency(purchaseAmount as number)}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminWishPurchased(props: { groupId: string }) {
  const { groupId } = props;
  const [isWishPurchased, setIsWishPurchased] = useState(false);

  return (
    <div className='field-container mb-3'>
      <h4 className='subtitle'>
        <FormattedMessage id='pages.active-group.wishes.not-purchased-title' />
      </h4>
      <div className='radio-container'>
        <label htmlFor={ERadio.YES} className='radio'>
          <input
            name={ERadio.YES}
            onChange={() => {
              setIsWishPurchased(true);
              history.push(
                replacePathParams(ERoutes.GROUP_IN_PROGRESS_WISH, {
                  groupId,
                }),
              );
            }}
            value={ERadio.YES}
            type='radio'
            id={ERadio.YES}
            className='hide'
            checked={isWishPurchased}
          />
          <span className='label'></span>
          <span className='main'>
            <FormattedMessage id='common.yes' />
          </span>
        </label>
        <label htmlFor={ERadio.NO} className='radio'>
          <input
            name={ERadio.NO}
            onChange={() => setIsWishPurchased(false)}
            value={ERadio.NO}
            type='radio'
            id={ERadio.NO}
            className='hide'
            checked={!isWishPurchased}
          />
          <span className='label'></span>
          <span className='main'>
            <FormattedMessage id='common.no' />
          </span>
        </label>
      </div>
    </div>
  );
}

function RenderWishes(props: any) {
  const {
    groupId,
    buyLimitDate,
    deliveryLimitDate,
    wishesList,
    admin,
    currentUserId,
    wishPurchased,
  }: IActiveGroupWishesProps = props;
  const dispatch = useDispatch();
  const [isNewWish, setIsNewWish] = useState(true);
  const [images, setImages] = useState<any>({});
  const [imagesToRemove, setImagesToRemove] = useState<string[] | undefined>(
    [],
  );
  const [showWishModal, setShowWishModal] = useState(false);
  const [propsWish, setPropsWish] = useState<any>(undefined);

  useEffect(() => scrollToTop(), [wishesList]);

  const handleWishModalShow = () => {
    setShowWishModal(true);
  };
  const handleWishModalClose = () => {
    setPropsWish(undefined);
    setImages({});
    setImagesToRemove([]);
    setIsNewWish(true);
    setShowWishModal(false);
  };

  function handleWishSubmit(values: any, wishId?: string) {
    if (isNewWish) {
      dispatch(GroupAction.newGroupWish(values, images, groupId));
    } else {
      const { description, name, notes, price, store, url } = values;
      dispatch(
        GroupAction.editGroupWish(
          { description, name, notes, price, store, url },
          images,
          imagesToRemove,
          wishId as string,
          groupId,
        ),
      );
    }
    handleWishModalClose();
  }

  function handleNewImages(body: any, name: string) {
    const newImage = images;
    newImage[name] = body;
    setImages(newImage);
  }

  function handleOldImages(ids: string[]) {
    setImagesToRemove(ids);
  }

  function likeItem(wishId: string) {
    dispatch(GroupAction.likeGroupWish(groupId, wishId));
  }

  function editItem(wish: IWish) {
    setPropsWish(wish);
    setIsNewWish(false);
    handleWishModalShow();
  }

  function deleteItem(wishId: string) {
    dispatch(
      returnNotification({
        type: ENotification.WARNING,
        msg1: 'warning.confirmation.message',
        dispatch: {
          func: GroupAction.deleteGroupWish,
          param: [wishId, groupId],
        },
      }),
    );
  }

  function handleSendGroupEmail(emailType: TGroupEmail) {
    dispatch(sendGroupEmail(emailType, groupId));
  }

  return (
    <div className={`tab-container active-group-presents-container`}>
      <div className='mb-5'>
        {!!admin && currentUserId === admin.id && !wishPurchased && (
          <AdminWishPurchased {...{ groupId }} />
        )}
        {!!wishPurchased && <WishPurchasedCard {...{ wishPurchased }} />}
      </div>
      <div className='mb-3'>
        <h4 className='subtitle'>
          <FormattedMessage id='pages.active-group.wishes.title' />
        </h4>
        <button
          className='add-btn mb-3'
          onClick={() => {
            setIsNewWish(true);
            handleWishModalShow();
          }}
        >
          <FontAwesomeIcon icon='plus-circle' />{' '}
          <FormattedMessage id='pages.wishlist.add_wish'></FormattedMessage>
        </button>
        {!!wishesList.length &&
          wishesList
            .sort(
              (a: GroupInterface.IGroupWish, b: GroupInterface.IGroupWish) =>
                b.likes - a.likes,
            )
            .map((groupWish: GroupInterface.IGroupWish, idx: number) => {
              const { wish, isLiked, likes } = groupWish;
              return (
                <WishCardComponent
                  key={idx}
                  wish={wish}
                  {...props}
                  readOnly={false}
                  isLiked={isLiked}
                  likes={likes}
                  likeItem={likeItem}
                  editItem={editItem}
                  deleteItem={deleteItem}
                />
              );
            })}
        {!wishesList.length && (
          <p className='text-primary'>
            <FormattedMessage id='pages.active-group.wishes.no_data' />
          </p>
        )}
      </div>
      <div className='dates'>
        <h4 className='subtitle'>
          <FormattedMessage id='pages.active-group.info.dates.title' />
        </h4>

        <div className='date-container mb-3'>
          <p className='mb-0'>
            <span>
              <FormattedMessage id='pages.active-group.info.dates.buyLimitDate' />
              {': '}
            </span>
            <br />
            <span className='text-primary'>
              {!!buyLimitDate ? (
                moment(buyLimitDate).format('DD/MM/YYYY')
              ) : (
                <FormattedMessage id='common.no-date' />
              )}
            </span>
          </p>
          {!!admin && currentUserId === admin.id && (
            <button
              disabled={!wishPurchased}
              className='btn-lili'
              onClick={() => handleSendGroupEmail(EGroupEmails.GIFT_BOUGHT)}
            >
              {' '}
              <FormattedMessage id='pages.active-group.info.dates.send_email_buyLimitDate' />
            </button>
          )}
        </div>
        <div className='date-container'>
          <p className='mb-0'>
            <span>
              <FormattedMessage id='pages.active-group.info.dates.deliveryLimitDate' />
              {': '}
            </span>
            <br />{' '}
            <span className='text-primary'>
              {!!deliveryLimitDate ? (
                moment(deliveryLimitDate).format('DD/MM/YYYY')
              ) : (
                <FormattedMessage id='common.no-date' />
              )}
            </span>
          </p>
          {!!admin && currentUserId === admin.id && (
            <button
              disabled={!wishPurchased}
              className='btn-lili'
              onClick={() => handleSendGroupEmail(EGroupEmails.GIFT_DELIVERED)}
            >
              {' '}
              <FormattedMessage id='pages.active-group.info.dates.send_email_deliveryLimitDate' />
            </button>
          )}
        </div>
      </div>
      {showWishModal && (
        <NewEditWishModalFormComponent
          {...props}
          showModal={showWishModal}
          handleClose={handleWishModalClose}
          propsWish={propsWish}
          handleWishSubmit={handleWishSubmit}
          handleNewImages={handleNewImages}
          handleOldImages={handleOldImages}
        />
      )}
    </div>
  );
}

function MemberPayment(props: any) {
  const {
    name,
    nickname,
    userPhoto,
    hasPaid,
    pendingPaymentId,
    active,
    isExternalPaid,
    handlePayment,
    handleSetHasPaid,
    isAdmin,
    isCurrentUserAdmin,
    isCurrentUser,
    isExternal,
    payLimitDate,
    isUsedMoneybox,
    amountType,
    amountPaid,
  } = props;

  const dateCheck =
    !payLimitDate || new Date() <= new Date(Date.parse(payLimitDate));

  const passedName = !!name ? name : nickname;

  const AdminSetAsPaid = () => (
    <div
      className='payment-button'
      onClick={() =>
        handleSetHasPaid(pendingPaymentId, passedName, isExternalPaid)
      }
    >
      <p>
        {isExternalPaid ? (
          <FontAwesomeIcon icon='times' />
        ) : (
          <FontAwesomeIcon icon='hand-holding-usd' />
        )}
        <span className='description'>
          {isExternalPaid ? (
            <FormattedMessage id='common.set-as-not-paid' />
          ) : (
            <FormattedMessage id='common.set-as-paid' />
          )}
        </span>
      </p>
    </div>
  );

  let PaymentMethod = (
    <div className='payment-buttons-container'>
      {isCurrentUserAdmin && isExternalPaid && dateCheck && !isUsedMoneybox && (
        <AdminSetAsPaid />
      )}
      {hasPaid && (
        <div className='payment-button secondary'>
          <p>
            {amountType === EFeeType.VARIABLE ||
            (amountType === EFeeType.HIDDEN &&
              (isCurrentUserAdmin || isCurrentUser)) ? (
              <span className='amount'>
                {formatNumberToCurrency(amountPaid)}
              </span>
            ) : (
              <FontAwesomeIcon icon='money-bill-wave' />
            )}
            <span className='description'>
              {isExternalPaid && (isCurrentUserAdmin || isCurrentUser) ? (
                <FormattedMessage id='common.paid-external' />
              ) : (
                <FormattedMessage id='common.paid' />
              )}
            </span>
          </p>
        </div>
      )}
    </div>
  );

  if (!hasPaid && dateCheck && !isUsedMoneybox) {
    PaymentMethod = (
      <div className='payment-buttons-container'>
        {isCurrentUserAdmin && <AdminSetAsPaid />}
        <div
          className='payment-button'
          onClick={() =>
            handlePayment(
              pendingPaymentId,
              passedName,
              isCurrentUser,
              isExternal,
            )
          }
        >
          <p>
            <FontAwesomeIcon icon='money-bill' />
            <span className='description'>
              <FormattedMessage id='common.pay' />
            </span>
          </p>
        </div>
      </div>
    );
  }

  if (!hasPaid && (!dateCheck || isUsedMoneybox)) {
    PaymentMethod = (
      <div className='payment-buttons-container'>
        <div className='payment-button danger'>
          <p>
            <FontAwesomeIcon icon='money-bill' />
            <span className='description'>
              <FormattedMessage id='common.not-paid' />
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`minicard ${
        isCurrentUser
          ? `user-type-${EUserType.SELF}`
          : active
          ? `user-type-${EUserType.CONTACT}`
          : `user-type-${EUserType.INACTIVE}`
      }`}
    >
      <div className='minicard-image'>
        {userPhoto ? (
          <div
            style={{
              backgroundImage: `url(${getAssetUrl.imagesStatic(userPhoto)})`,
            }}
          />
        ) : (
          <div className='icon-container d-flex justify-container-center align-items-center'>
            <FontAwesomeIcon icon='user' />
          </div>
        )}
      </div>
      <div className='minicard-info'>
        <h6 className='capitalize'>{!!name ? name : nickname}</h6>
        {isAdmin ? (
          <p>
            ( <FormattedMessage id={`common.user-type-${EUserType.ADMIN}`} /> )
          </p>
        ) : isExternal ? (
          <p>
            ( <FormattedMessage id={`common.user-type-${EUserType.EXTERNAL}`} />{' '}
            )
          </p>
        ) : active ? (
          !!name ? (
            <p>{nickname}</p>
          ) : null
        ) : (
          <p>
            ( <FormattedMessage id={`common.user-type-${EUserType.INACTIVE}`} />{' '}
            )
          </p>
        )}
      </div>
      <div className='right-container'>{PaymentMethod}</div>
    </div>
  );
}

function RenderMoneybox(props: GroupInterface.IActiveGroupMoneyboxProps) {
  const { groupId, currentUserId, activeGroup, memberList, moneybox } = props;
  const dispatch = useDispatch();
  const { admin, isCurrentUserAdmin, id, isUsedMoneybox } = activeGroup;
  const {
    amount: propsAmount,
    feeType,
    payLimitDate,
    amountReturnType,
    amountRaisedByLiliApp,
    amountRaised: totalAmount,
  } = moneybox;
  const date = moment(payLimitDate).format('DD/MM/YYYY');
  const amount = !!propsAmount ? propsAmount : 0;
  const amountType: TFeeType = feeType;
  const [showTpvModal, setShowTpvModal] = useState(false);
  const [selectedPendingPaymentId, setSelectedPendingPaymentId] = useState('');
  const [showHasPaidModal, setShowHasPaidModal] = useState(false);
  const [selectedMemberName, setSelectedMemberName] = useState('');

  const handlePayment = (
    ppId: string,
    nn: string,
    isCurrentUser: boolean,
    isExternal?: boolean,
  ) => {
    setSelectedPendingPaymentId(ppId);
    if (isCurrentUser) {
      handleTpvModalShow();
      return;
    } else {
      const notification: INotification = {
        type: ENotification.WARNING,
        msg1: 'pages.active-group.moneybox.warning-payment-main',
        value1: <span className='text-primary text-capitalize'>{nn}</span>,
        msg2: isExternal
          ? 'pages.active-group.moneybox.warning-payment-sub-external'
          : 'pages.active-group.moneybox.warning-payment-sub',
        func: {
          func: handleTpvModalShow,
        },
      };
      dispatch(returnNotification(notification));
    }
  };

  const handleTpvModalShow = () => {
    setShowTpvModal(true);
  };

  const handleTpvModalClose = () => {
    dispatch(GroupAction.loadGroupInfo(groupId));
    dispatch(GroupAction.loadGroupMembers(groupId));
    dispatch(GroupAction.loadGroupMoneybox(groupId));
    setSelectedPendingPaymentId('');
    setShowTpvModal(false);
  };

  const handleHasPaidModalShow = (ppId: string, memberName: string) => {
    setSelectedPendingPaymentId(ppId);
    setSelectedMemberName(memberName);
    setShowHasPaidModal(true);
  };

  const handleHasPaidModalClose = () => {
    setSelectedPendingPaymentId('');
    setSelectedMemberName('');
    setShowHasPaidModal(false);
  };

  const handleSetHasPaid = (ppId: string, nn: string, hasPaid: boolean) => {
    const notification: INotification = {
      type: ENotification.WARNING,
      msg1: hasPaid
        ? 'pages.active-group.moneybox.warning-payment-external-remove'
        : 'pages.active-group.moneybox.warning-payment-external-add',
      value1: <span className='text-primary text-capitalize'>{nn}</span>,
    };
    if (
      (amountType === EFeeType.VARIABLE || amountType === EFeeType.HIDDEN) &&
      !hasPaid
    ) {
      notification.func = {
        func: handleHasPaidModalShow,
        param: [ppId, nn],
      };
    } else {
      notification.dispatch = {
        func: setAsPaidGroupMember,
        param: [groupId, ppId],
      };
    }
    dispatch(returnNotification(notification));
  };

  function handleSendGroupEmail(emailType: TGroupEmail) {
    dispatch(sendGroupEmail(emailType, groupId));
  }

  return (
    <div>
      {!!amount ||
      amountType === EFeeType.VARIABLE ||
      amountType === EFeeType.HIDDEN ? (
        <div className={`tab-container active-group-moneybox-container`}>
          <div className='md-card d-flex align-items-center flex-column text-center'>
            <PiggyComponent amount={totalAmount} isBroken={isUsedMoneybox} />
            {!!totalAmount && isCurrentUserAdmin && (
              <p className='mt-3'>
                <FormattedMessage
                  id='pages.active-group.moneybox.external'
                  values={{
                    value: (
                      <span className='text-primary'>
                        {formatNumberToCurrency(amountRaisedByLiliApp)}
                      </span>
                    ),
                  }}
                />
              </p>
            )}
            <p>
              <span>
                <FormattedMessage id='pages.active-group.info.dates.payLimitDate' />
                {': '}
              </span>
              <br />
              {!!payLimitDate ? (
                <span className='text-primary'>{date}</span>
              ) : (
                <span className='text-primary'>
                  <FormattedMessage id='common.waiting-admin' />
                </span>
              )}
            </p>
            {amountReturnType !== null && amountReturnType !== undefined && (
              <p>
                <span>
                  <FormattedMessage id='pages.active-group.moneybox.refound' />
                  {': '}
                </span>
                <br />
                <span className='text-primary'>
                  <FormattedMessage id={`common.refound.${amountReturnType}`} />
                  {amountReturnType === ERefound.TO_ONG && (
                    <a
                      href={socialProjectsLink.link}
                      aria-label={socialProjectsLink.name}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <FontAwesomeIcon icon='info-circle' />
                    </a>
                  )}
                </span>
              </p>
            )}
            {!!amount && (
              <p>
                <span>
                  <FormattedMessage
                    id={
                      amountType === EFeeType.FIXED
                        ? 'pages.active-group.moneybox.amount_fixed'
                        : 'pages.active-group.moneybox.amount_variable'
                    }
                  />
                  {': '}
                </span>
                <span className='text-primary'>
                  {formatNumberToCurrency(amount)}
                </span>
              </p>
            )}
            {isCurrentUserAdmin && !isUsedMoneybox && (
              <div className='admin-buttons-container'>
                <button
                  className='btn-lili'
                  onClick={() =>
                    handleSendGroupEmail(EGroupEmails.GIFT_REMINDER)
                  }
                >
                  <p className='first-line'>
                    <FormattedMessage id='pages.active-group.moneybox.button-send-mail-first-line' />
                  </p>
                  <p className='second-line'>
                    <FormattedMessage id='pages.active-group.moneybox.button-send-mail-second-line' />
                  </p>
                </button>
                <button
                  className='btn-lili'
                  disabled={amountRaisedByLiliApp <= 0}
                  onClick={() =>
                    history.push(
                      replacePathParams(ERoutes.GROUP_IN_PROGRESS_MONEYBOX, {
                        groupId: id,
                      }),
                    )
                  }
                >
                  <p className='first-line'>
                    <FormattedMessage id='pages.active-group.moneybox.button-finalize-first-line' />
                  </p>
                  <p className='second-line'>
                    <FormattedMessage id='pages.active-group.moneybox.button-finalize-second-line' />
                  </p>
                </button>
              </div>
            )}
          </div>
          <div>
            {!!admin && (
              <MemberPayment
                id={admin.id}
                name={admin.name}
                nickname={admin.nickname}
                userPhoto={admin.userPhoto}
                hasPaid={admin.hasPaid}
                pendingPaymentId={admin.pendingPaymentId}
                active={admin.active}
                handlePayment={handlePayment}
                handleSetHasPaid={handleSetHasPaid}
                groupId={groupId}
                isAdmin={true}
                isCurrentUserAdmin={isCurrentUserAdmin}
                isExternalPaid={admin.isExternalPaid}
                isCurrentUser={admin.id === currentUserId}
                payLimitDate={payLimitDate}
                isUsedMoneybox={isUsedMoneybox}
                amountType={amountType}
                amountPaid={admin.amountPaid}
              />
            )}
            {!!memberList.length &&
              memberList.map((member: GroupInterface.IGroupMember) => {
                return (
                  <MemberPayment
                    key={member.nickname}
                    id={member.id}
                    name={member.name}
                    nickname={member.nickname}
                    userPhoto={member.userPhoto}
                    hasPaid={member.hasPaid}
                    pendingPaymentId={member.pendingPaymentId}
                    active={member.active}
                    handlePayment={handlePayment}
                    handleSetHasPaid={handleSetHasPaid}
                    groupId={groupId}
                    isAdmin={false}
                    isCurrentUserAdmin={isCurrentUserAdmin}
                    isExternalPaid={member.isExternalPaid}
                    isCurrentUser={member.id === currentUserId}
                    isExternal={member.isExternal}
                    payLimitDate={payLimitDate}
                    isUsedMoneybox={isUsedMoneybox}
                    amountType={amountType}
                    amountPaid={member.amountPaid}
                  />
                );
              })}
            {showTpvModal && (
              <TpvModalFormComponent
                {...props}
                showModal={showTpvModal}
                handleClose={handleTpvModalClose}
                pendingPaymentId={selectedPendingPaymentId}
                variableAmount={
                  amountType === EFeeType.VARIABLE ||
                  amountType === EFeeType.HIDDEN
                }
                amount={amount}
              />
            )}
            {showHasPaidModal && (
              <SetHasPaidModalFormComponent
                {...props}
                showModal={showHasPaidModal}
                handleClose={handleHasPaidModalClose}
                pendingPaymentId={selectedPendingPaymentId}
                groupId={groupId}
                amount={amount}
                memberName={selectedMemberName}
              />
            )}
          </div>
        </div>
      ) : (
        <p className='text-primary'>
          <FormattedMessage id='pages.active-group.moneybox.no_data' />
        </p>
      )}
    </div>
  );
}

export function ActiveGroupComponent(props: any) {
  const dispatch = useDispatch();
  const groupId = props.match.params.groupId;
  const currentUser = useSelector(makeSelectCurrentUser());
  const { id: currentUserId } = currentUser;
  const activeGroup: GroupInterface.IGroupInfo = useSelector(
    GroupSelector.makeSelectGroupInfo(),
  );
  const memberList: GroupInterface.IGroupMember[] = useSelector(
    GroupSelector.makeSelectGroupMembers(),
  );
  const wishesList: GroupInterface.IGroupWish[] = useSelector(
    GroupSelector.makeSelectGroupWishes(),
  );
  const moneybox: GroupInterface.IGroupMoneybox = useSelector(
    GroupSelector.makeSelectGroupMoneybox(),
  );
  const { name, photo, admin, dates } = activeGroup;
  const { buyLimitDate, deliveryLimitDate } = { ...dates };
  const activeGroupConfig: GroupInterface.IActiveGroupConfig[] = [
    {
      name: 'participants',
      icon: 'user-friends',
      Component: RenderMembers,
      props: {
        groupId: groupId,
        admin: admin,
        currentUserId: currentUserId,
        memberList: memberList,
      } as GroupInterface.IActiveGroupMembersProps,
    },
    {
      name: 'presents',
      image: 'REGALO_AMARILLO.svg',
      Component: RenderWishes,
      props: {
        groupId: groupId,
        buyLimitDate: buyLimitDate,
        deliveryLimitDate: deliveryLimitDate,
        wishesList: wishesList,
        currentUserId: currentUserId,
        admin: admin,
        wishPurchased: activeGroup.wishPurchased,
      } as GroupInterface.IActiveGroupWishesProps,
    },
    {
      name: 'moneybox',
      icon: 'piggy-bank',
      Component: RenderMoneybox,
      props: {
        groupId: groupId,
        currentUserId: currentUserId,
        activeGroup: activeGroup,
        memberList: memberList,
        moneybox: moneybox,
      } as GroupInterface.IActiveGroupMoneyboxProps,
    },
  ];
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [activeTab, setActiveTab] = useState(activeGroupConfig[activeIdx]);

  useEffect(() => {
    dispatch(GroupAction.loadGroupInfo(groupId));
    dispatch(GroupAction.loadGroupMembers(groupId));
    dispatch(GroupAction.loadGroupWishes(groupId));
    dispatch(GroupAction.loadGroupMoneybox(groupId));
    return () => {
      dispatch(
        GroupAction.resetGroup(GroupInterface.EGroupStateKeys.groupInfo),
      );
      dispatch(
        GroupAction.resetGroup(GroupInterface.EGroupStateKeys.groupMembers),
      );
      dispatch(
        GroupAction.resetGroup(GroupInterface.EGroupStateKeys.groupWishes),
      );
      dispatch(
        GroupAction.resetGroup(GroupInterface.EGroupStateKeys.groupMoneybox),
      );
    };
    // eslint-disable-next-line
  }, []);

  // eslint-disable-next-line
  useEffect(() => setActiveTab(activeGroupConfig[activeIdx]), [
    activeIdx,
    activeGroup,
    memberList,
    wishesList,
    moneybox,
  ]);
  useEffect(
    () => setActiveIdx(navigateWithHash(props.location.hash)),
    // eslint-disable-next-line
    [props.location.hash],
  );

  function navigateWithHash(hash: string): number {
    if (!hash) {
      return 0;
    }
    const label = hash.replace('#', '');
    const index = activeGroupConfig.findIndex(
      (el: GroupInterface.IActiveGroupConfig) => el.name === label,
    );
    return index;
  }

  return (
    <div className='page-container active-group-container'>
      <NavbarComponent />
      <NavigationMenuComponent {...props} />
      <div className='active-group-header'>
        <div
          className='image-container'
          style={{
            backgroundImage: !!photo
              ? `url(${getAssetUrl.imagesStatic(photo)})`
              : `url(${getAssetUrl.icons('REGALO_AMARILLO.svg')})`,
            backgroundSize: !!photo ? 'cover' : '70%',
          }}
        ></div>
        <div className='right'>
          <h4 className='subtitle'>{name}</h4>
          <ul
            className='nav nav-tabs nav-fill md-card'
            id='pills-tab'
            role='tablist'
          >
            {RenderNav(activeGroupConfig, setActiveIdx, activeIdx)}
          </ul>
        </div>
      </div>
      <div className='tab-content content-wrapper' id='pills-tabContent'>
        <RenderTab {...activeTab} />
      </div>
      <div className='chat-button-container'>
        <Link to={replacePathParams(ERoutes.CHAT, { groupId: groupId })}>
          <button type='button' className='btn btn-secondary btn-circle'>
            <FontAwesomeIcon icon='comment-alt' size='2x' />
          </button>
        </Link>
      </div>
    </div>
  );
}
